import clsx from 'clsx';
import { Trash } from '@music-vine/cadence/icons';
import React, { useState } from 'react';
import { Button } from '@music-vine/cadence/ui';
import ChannelImage from '@/components/YouTubeChannelImage';
import { ChannelResults } from './ChannelResults';
import { useSearchYoutubeChannel } from './hooks/useSearchYoutubeChannel';
import { useYoutubeChannelData } from './hooks/useYoutubeChannelData';
import { RemoveSafelistDialog } from './RemoveSafelistDialog';
import type { ChangeEventHandler, ReactNode } from 'react';
import type { YouTubeChannelData, YouTubeChannelRequest } from './types';
interface ChannelSafelistProps {
  className?: string;
  compact?: boolean;
  disabled?: boolean;
  handleRemoveChannel: (channelID: number) => void;
  hideTrashIcon?: boolean;
  isEditing: boolean;
  isFetching?: boolean;
  isPaused: boolean;
  isRemoving?: boolean;
  selectedChannel: string;
  whitelistChannel: (channel: YouTubeChannelRequest) => void;
  whitelistId?: number;
}
const ChannelSafelistItem = ({
  className,
  channelData,
  children
}: {
  className?: string;
  channelData?: YouTubeChannelData | null;
  children?: ReactNode;
}) => {
  if (!channelData) {
    return <div className={clsx('group flex h-full w-full items-center justify-between gap-3 rounded-lg bg-gray-100 p-3 dark:bg-gray-800', '@[250px]:gap-4', className)}>
        <ChannelImage channel={{
        channelId: '',
        channelName: ''
      }} className="h-10" />
        <div className="me-auto max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
          <p className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-sans font-semibold @[250px]:text-base text-sm">
            Unknown Channel
          </p>
        </div>
        {children}
      </div>;
  }
  return <a className={clsx('group flex h-full w-full cursor-pointer items-center justify-between gap-3 rounded-lg bg-gray-100 p-3 hover:bg-gray-200 group-hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700', '@[250px]:gap-4', className)} href={`https://www.youtube.com/channel/${channelData.channelId}`} rel="noreferrer" target="_blank" data-sentry-component="ChannelSafelistItem" data-sentry-source-file="ChannelWhitelist.tsx">
      {/* Fixed height, not h-full: in the safelist popover every ancestor is
          auto-height, so the avatar's h-full collapsed it to 0 (DEV-6638). */}
      <ChannelImage channel={{
      channelId: channelData?.channelId ?? '',
      channelName: channelData?.title ?? ''
    }} className="h-10" data-sentry-element="ChannelImage" data-sentry-source-file="ChannelWhitelist.tsx" />
      <div className="me-auto max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
        <div className="group block max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-sans font-semibold @[250px]:text-base text-gray-950 text-sm hover:underline md:text-xs 2xl:text-sm dark:text-white">
          {channelData.title}
        </div>
        <p className="@[250px]:text-base text-gray-700 text-sm md:text-xs 2xl:text-sm dark:text-gray-200">
          {channelData.customUrl ? channelData.customUrl : channelData.channelId}
        </p>
      </div>
      {children}
    </a>;
};
export const ChannelSafelist: React.FC<ChannelSafelistProps> = React.memo(({
  handleRemoveChannel,
  isEditing,
  selectedChannel,
  whitelistId,
  whitelistChannel,
  isFetching,
  isRemoving,
  className,
  compact,
  hideTrashIcon,
  disabled,
  isPaused
}) => {
  const [inputValue, setInputValue] = useState('');
  const {
    data: searchResults
  } = useSearchYoutubeChannel(inputValue);
  const {
    data: channelData
  } = useYoutubeChannelData(selectedChannel);
  const handleSelectChannel = (selectedValue: YouTubeChannelRequest) => {
    if (disabled) {
      return;
    }
    whitelistChannel(selectedValue);
    setInputValue('');
  };
  const handleSubmitChannelID = (id: string) => {
    if (disabled) {
      return;
    }
    const selectedValue = {
      channelId: id,
      channelTitle: 'Unknown'
    };
    whitelistChannel(selectedValue);
  };
  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    if (typeof event?.target?.value === 'string') {
      setInputValue(event.target.value);
    }
  };
  const showResults = searchResults && searchResults?.length > 0;
  const hasError = !channelData;
  if (showResults || isEditing) {
    return <ChannelResults compact={compact} disabled={disabled || isPaused} handleChange={handleChange} handleSelectChannel={handleSelectChannel} handleSubmitChannelID={handleSubmitChannelID} inputValue={inputValue} searchResults={searchResults ?? []} isPaused={isPaused} />;
  }
  if (hasError) {
    return <ChannelSafelistItem className={className} />;
  }
  return <ChannelSafelistItem channelData={channelData}>
        {(!!whitelistId || isFetching) && !hideTrashIcon && !isPaused && <div onClick={e => {
      e.preventDefault();
      e.stopPropagation();
    }}>
            <RemoveSafelistDialog channelName={channelData?.title ?? 'this channel'} isRemoving={isRemoving} onConfirm={() => {
        if (whitelistId) {
          handleRemoveChannel(whitelistId);
        }
      }}>
              <Button borderRadius="full" disabled={isFetching || isRemoving || disabled} size="icon" variant="contrast">
                <Trash className="size-4" />
                <span className="sr-only">Remove</span>
              </Button>
            </RemoveSafelistDialog>
          </div>}
      </ChannelSafelistItem>;
});
ChannelSafelist.displayName = 'ChannelWhitelist';